import React from "react";
import { 
  BarChart3, FileText, Calendar, CheckCircle2, AlertCircle, ShieldCheck, 
  ArrowRight, Search, MapPin
} from "lucide-react";
import { getReportBySlug, ReportRecord } from "../../utils/reportRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface ReportPageProps {
  reportSlug: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function ReportPage({ reportSlug, onNavigate, onLogin = () => {}, currentUser }: ReportPageProps) {
  const report: ReportRecord | null = getReportBySlug(reportSlug);

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Report Not Found</h1>
        <p className="text-sm text-slate-600 mb-4">The requested market report does not exist or is unavailable.</p>
        <button
          onClick={() => onNavigate("/reports")}
          className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-medium text-xs hover:bg-indigo-700 transition"
        >
          View Market Reports Directory
        </button>
      </div>
    );
  }

  const currentRoute = `/reports/${report.slug}`;

  useSeoMetadata({
    title: report.title,
    description: report.metaDescription,
    canonicalUrl: `https://getbiznearyou.com${currentRoute}`,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": report.h1,
      "description": report.metaDescription,
      "datePublished": "2026-01-15",
      "dateModified": "2026-07-23",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://getbiznearyou.com${currentRoute}`
      },
      "publisher": {
        "@type": "Organization",
        "name": "BizNearYou"
      }
    }
  });

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="flex-grow max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs
          items={[
            { label: "Reports", path: "/reports" },
            { label: report.h1, path: currentRoute }
          ]}
          onNavigate={onNavigate}
        />

        {/* Report Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700">
              {report.reportingPeriod}
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Published July 2026</span>
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-4">
            {report.h1}
          </h1>

          <p className="text-xs sm:text-sm text-slate-600 bg-slate-100 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-slate-800">Sample & Scope:</strong> {report.sampleSizeSummary}
          </p>
        </header>

        {/* Highlight Metrics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {report.dataPoints.map((dp, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4 text-center shadow-sm">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1 truncate">{dp.label}</span>
              <span className="text-xl sm:text-2xl font-extrabold text-indigo-600 block mb-1">{dp.value}</span>
              <span className="text-[10px] text-slate-600 block line-clamp-2">{dp.description}</span>
            </div>
          ))}
        </div>

        {/* Key Findings Box */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>Key Research Findings</span>
          </h2>
          <ul className="space-y-2.5">
            {report.keyFindings.map((finding, i) => (
              <li key={i} className="text-xs sm:text-sm text-slate-700 flex items-start gap-2.5">
                <span className="font-bold text-indigo-600 flex-shrink-0">{i + 1}.</span>
                <span>{finding}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Report Content Sections */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 mb-8 space-y-8 shadow-sm">
          {report.contentSections.map((sec, i) => (
            <section key={i} className="space-y-3">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">{sec.heading}</h2>
              {sec.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  {para}
                </p>
              ))}
            </section>
          ))}

          {/* Methodology & Limitations Disclosure */}
          <div className="mt-8 pt-8 border-t border-slate-200 space-y-4">
            <div>
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-1">Methodology Overview</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{report.methodologyOverview}</p>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-slate-800 block mb-0.5">Data Limitations & Privacy:</span>
                <span>{report.limitationsDisclaimer}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Banner */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 text-center mb-8 shadow-sm">
          <h2 className="text-lg sm:text-xl font-bold mb-2 text-slate-900 dark:text-white">Explore Local Business Profiles Featured in This Report</h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-5">
            Search physically operating employers, view contactability scores, and launch direct outreach.
          </p>
          <a
            href="/find-jobs"
            onClick={(e) => handleLinkClick(e, "/find-jobs")}
            className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs transition inline-block cursor-pointer shadow-xs"
          >
            Launch Search Engine
          </a>
        </div>

        <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
      </main>
    </div>
  );
}
